import React, { useState } from "react";
import useTestSeries from "../useTestSeries";
import EditImg from "../../../images/SideBar/Edit";
import Loader from "../../../components/Loader";
import CommonDataGrid from "../../../components/CommonDataGrid";
import Button from "../../../components/Botton";
import { Toaster } from "react-hot-toast";
import { GridColDef } from "@mui/x-data-grid";
import { TextField, Chip } from "@mui/material";
import DeleteImg from "../../../images/SideBar/Delete";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import EditTestSeriesIndex from "./EditTestSeries";
import { getSortByTimeButtonText } from "../../../util/getSortByTimeText";
import { hasPermission } from "../../../utils/permissions";
import { useAppSelector } from "../../../store/hooks";
import { selectUser } from "../../../store/slices/authSlice";
import { getDeleteConfirmationMessage } from "../../../utils/pagePermissions";

const TestSeries: React.FC = () => {
  const user = useAppSelector(selectUser);
  const canEdit = hasPermission(user, "test-series", "update");
  const canAdd = hasPermission(user, "test-series", "create");
  const canDelete = hasPermission(user, "test-series", "delete");

  const columns: GridColDef[] = [
    {
      field: "isPaid",
      headerName: "Type",
      align: "center",
      headerAlign: "center",
      flex: 0.8,
      filterable: true,
      sortable: true,
      renderCell: (params) => (
        <Chip
          label={params.value ? "⭐ Premium" : "Free"}
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: "0.75rem",
            minWidth: "95px",
            height: "26px",
            borderRadius: "6px",
            letterSpacing: "0.3px",
            ...(params.value
              ? {
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "#ffffff",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
                },
              }
              : {
                background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                color: "#ffffff",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                boxShadow: "0 2px 8px rgba(17, 153, 142, 0.3)",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(17, 153, 142, 0.4)",
                },
              }),
          }}
        />
      ),
    },
    {
      field: "testSeriesName",
      headerName: "Name",
      align: "left",
      headerAlign: "center",
      flex: 1.5,
      filterable: true,
      sortable: true,
    },
    {
      field: "courseName",
      headerName: "Stream",
      align: "center",
      headerAlign: "center",
      flex: 1,
      filterable: true,
      sortable: true,
    },
    {
      field: "standard",
      headerName: "Education",
      align: "left",
      headerAlign: "center",
      flex: 1,
      filterable: true,
      sortable: true,
    },
    {
      field: "languageName",
      headerName: "Language",
      align: "center",
      headerAlign: "center",
      flex: 0.8,
      filterable: true,
      sortable: true,
    },
    {
      field: "totalMarks",
      headerName: "Marks",
      align: "center",
      headerAlign: "center",
      flex: 0.6,
      type: "number",
      filterable: true,
      sortable: true,
    },
    {
      field: "duration",
      headerName: "Duration (Min)",
      align: "center",
      headerAlign: "center",
      flex: 0.8,
      type: "number",
      filterable: true,
      sortable: true,
      valueGetter: (params: any) => Math.floor(params / 60), // Convert seconds to minutes for display
    },
    {
      field: "amount",
      headerName: "Amount",
      align: "center",
      headerAlign: "center",
      flex: 0.6,
      type: "number",
      filterable: true,
      sortable: true,
    },
    {
      field: "levels",
      headerName: "Level(s)",
      align: "center",
      headerAlign: "center",
      flex: 0.8,
      type: "string",
      filterable: false,
      sortable: true,
    },
    {
      field: "subject",
      headerName: "Subject(s)",
      align: "center",
      headerAlign: "center",
      flex: 1.2,
      type: "string",
      filterable: false,
      sortable: true,
    },
  ];

  if (canEdit || canDelete) {
    columns.push({
      field: "Actions",
      headerName: "Actions",
      align: "center",
      headerAlign: "center",
      flex: 0.8,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div className="flex h-full items-center justify-center gap-3">
          {canEdit && (
            <button
              onClick={() => handleEdit(params.row._id)}
              className="text-fontColor hover:text-primary-2"
            >
              <EditImg />
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => openDeleteModal(params.row._id)}
              className="text-fontColor hover:text-red-500"
            >
              <DeleteImg />
            </button>
          )}
        </div>
      ),
    });
  }

  const {
    selectedTestSeriesId,
    testSeriesData,
    loading,
    searchQuery,
    setSearchQuery,
    handleEdit,
    handleDelete,
    handleStatusUp,
    handleDateFilter,
    setFilterModel,
    setSortingModel,
    paginationModel,
    setPaginationModel,
    rowCount,
    handleAdd,
    timeSortOrder,
    // Destructure new items from the hook
    handleSortByTime,
  } = useTestSeries(columns);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [testSeriesToDelete, setTestSeriesToDelete] = useState<string | null>(
    null
  );

  const openDeleteModal = (id: string) => {
    setTestSeriesToDelete(id);
    setIsModalOpen(true);
  };

  const confirmDelete = () => {
    if (testSeriesToDelete) {
      handleDelete(testSeriesToDelete);
      setIsModalOpen(false);
    }
  };

  const closeDeleteModal = () => {
    setIsModalOpen(false);
    setTestSeriesToDelete(null);
  };

  return (
    <div className="mx-auto p-4">
      {loading && <Loader />}
      {selectedTestSeriesId && canEdit ? (
        <EditTestSeriesIndex testSeriesId={selectedTestSeriesId} />
      ) : (
        <div className="space-y-4 mt-4 bg-white p-4 rounded-b-lg shadow-md">
          {/* Updated this section to include the new button */}
          <div className="flex justify-between items-center gap-4 mr-1 mb-4">
            <TextField
              fullWidth
              variant="outlined"
              label="Search"
              placeholder="Search By"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ maxWidth: '400px' }}
            />
            <div className="flex gap-4">
              <Button
                onClick={handleSortByTime}
                className="py-1.5 px-9 rounded"
                children={getSortByTimeButtonText(timeSortOrder)}
                type="button"
              />
              {canAdd && (
                <Button
                  onClick={handleAdd}
                  className="py-1.5 px-9 rounded"
                  children="Add Test Series"
                  type="button"
                />
              )}
            </div>
          </div>
          <CommonDataGrid
            id="testSeries"
            rows={testSeriesData}
            columns={columns}
            loading={loading}
            showConfirmation={false}
            confirmationMessage={""}
            setFilterModel={setFilterModel}
            setSortingModel={setSortingModel}
            paginationMode="server"
            rowCount={rowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            onCancelDelete={function (): void {
              throw new Error("Function not implemented.");
            }}
            onConfirmDelete={function (): void {
              throw new Error("Function not implemented.");
            }}
            toolbarProps={{ toolbar: { showQuickFilter: true } }}
          />
        </div>
      )}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        message={getDeleteConfirmationMessage("test-series")}
      />
      <Toaster />
    </div>
  );
};

export default TestSeries;